import { css } from "@emotion/css";
import { Callout, InlineMessage, RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import { ArrowRotateIcon } from "@octopusdeploy/design-system-icons";
import { fontSize, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import * as React from "react";
import semver from "semver/preload";
import { TemplateUpdateDialog } from "~/areas/projects/components/Templates/TemplateUpdateDialog";
import { ExpandableFormSection, FormSection, FormSectionHeading, Summary } from "~/components/form/index";
export enum UsageConstraint {
    Stable = "Stable",
    PreRelease = "PreRelease"
}
export enum UpdateBehaviour {
    Minor = "Minor",// Includes Minor and Patch updates
    Patch = "Patch",// Patch updates only
    PreRelease = "PreRelease",
    Invalid = "Invalid"
}
export const getPreferencesSummary = (selectedUsageConstraint: UsageConstraint, selectedUpdateBehaviour: UpdateBehaviour) => {
    if (selectedUsageConstraint === UsageConstraint.PreRelease) {
        return Summary.summary("Template usage will use the latest pre-release version");
    }
    if (selectedUpdateBehaviour === UpdateBehaviour.Minor) {
        return Summary.summary("Template usage will automatically update whenever a patch or minor version is published.");
    }
    if (selectedUpdateBehaviour === UpdateBehaviour.Patch) {
        return Summary.summary("Template usage will automatically update whenever a patch version is published");
    }
    return Summary.placeholder("No preference selected");
};
export function mapMaskToUsageConstraint(value: string): UsageConstraint {
    if (value.toLowerCase() === "prerelease") {
        return UsageConstraint.PreRelease;
    }
    return UsageConstraint.Stable;
}
export function mapMaskToUpdateBehaviour(mask: string): UpdateBehaviour {
    switch (true) {
        case mask.toLowerCase() === "prerelease":
            return UpdateBehaviour.PreRelease;
        case mask === "":
        case /^\d+\.X$/i.test(mask):
            return UpdateBehaviour.Minor;
        case /^\d+\.\d+\.X$/i.test(mask):
            return UpdateBehaviour.Patch;
        default:
            return UpdateBehaviour.Invalid;
    }
}
export function getVersionMask(version: string, updateBehaviour: UpdateBehaviour): string {
    const major = semver.major(version);
    const minor = semver.minor(version);
    switch (updateBehaviour) {
        case UpdateBehaviour.Invalid:
        case UpdateBehaviour.Minor:
            return `${major}.X`;
        case UpdateBehaviour.Patch:
            return `${major}.${minor}.X`;
        case UpdateBehaviour.PreRelease:
            return "PreRelease";
        default:
            exhaustiveCheck(updateBehaviour, "Invalid UpdateBehaviour");
    }
}
export function getUpdatedVersionMask(latestVersion: string, originalVersion: string | undefined | null, updateBehaviour: UpdateBehaviour, originalUpdateBehaviour: UpdateBehaviour): string {
    if (!originalVersion)
        return getVersionMask(latestVersion, updateBehaviour);
    if (updateBehaviour === originalUpdateBehaviour)
        return getVersionMask(originalVersion, updateBehaviour);
    const isMinorToPatchOrViceVersa = (originalUpdateBehaviour === UpdateBehaviour.Minor && updateBehaviour === UpdateBehaviour.Patch) || (originalUpdateBehaviour === UpdateBehaviour.Patch && updateBehaviour === UpdateBehaviour.Minor);
    if (isMinorToPatchOrViceVersa) {
        return getVersionMask(originalVersion, updateBehaviour);
    }
    else {
        return getVersionMask(latestVersion, updateBehaviour);
    }
}
function OnlyPreReleaseCallout() {
    return <Callout type={"information"} title={"Only pre-release versions of this template are available."}/>;
}
interface TemplateVersioningSectionProps {
    sectionTitle: string;
    templateName: string;
    currentVersion?: string;
    latestStableVersion?: string | null;
    latestPreReleaseVersion?: string | null;
    isOnlyPreRelease: boolean;
    selectedUsageConstraint: UsageConstraint;
    selectedUpdateBehaviour: UpdateBehaviour;
    onUsageConstraintChanged: (value: UsageConstraint) => void;
    onUpdateBehaviourChanged: (value: UpdateBehaviour) => void;
    onUpdateToLatestVersion: () => void;
    isFeatureEnabled?: boolean;
}
export function TemplateVersioningSection({ sectionTitle, templateName, currentVersion, latestStableVersion, latestPreReleaseVersion, isOnlyPreRelease, selectedUsageConstraint, selectedUpdateBehaviour, onUsageConstraintChanged, onUpdateBehaviourChanged, onUpdateToLatestVersion, isFeatureEnabled = true, }: TemplateVersioningSectionProps) {
    const [isUpdateDialogOpen, setIsUpdateDialogOpen] = React.useState(false);
    const isUpdateAvailable = currentVersion && latestStableVersion ? semver.ltr(currentVersion, latestStableVersion) : false;
    const handleOpenUpdateDialog = () => {
        setIsUpdateDialogOpen(true);
    };
    const handleCloseUpdateDialog = () => {
        setIsUpdateDialogOpen(false);
    };
    return (<>
            <FormSectionHeading title="Versioning"/>
            <FormSection title={isUpdateAvailable && isFeatureEnabled ? (<span className={styles.templateVersionTitle}>
                            Template version
                            <ArrowRotateIcon color={themeTokens.color.icon.info} size={20}/>
                        </span>) : ("Template version")} help={<>{currentVersion && <span>Currently using: v{currentVersion}</span>}</>}>
                {isUpdateAvailable && isFeatureEnabled && (<>
                        <InlineMessage type="information" title="Version update:" description={`v${latestStableVersion} of ${templateName} is available.`} action={{
                label: "Review and update",
                onClick: handleOpenUpdateDialog,
            }}/>
                        <TemplateUpdateDialog open={isUpdateDialogOpen} onClose={handleCloseUpdateDialog} onUpdateVersion={onUpdateToLatestVersion} currentVersion={currentVersion} latestVersion={latestStableVersion}/>
                    </>)}
            </FormSection>
            <ExpandableFormSection title="Versioning preferences" summary={getPreferencesSummary(selectedUsageConstraint, selectedUpdateBehaviour)} help={<span>Choose how you want to receive updates for this template.</span>} errorKey="Versioning preferences" isExpandedByDefault={isOnlyPreRelease}>
                {latestPreReleaseVersion && (<>
                        <RadioButtonGroup value={selectedUsageConstraint} onChange={onUsageConstraintChanged} title="Select usage constraint" accessibleName="Usage constraint">
                            <RadioButton label="Stable release" value={UsageConstraint.Stable} disabled={isOnlyPreRelease}/>
                            <RadioButton label="Pre-release" value={UsageConstraint.PreRelease}/>
                            <span className={styles.helperText}>Current pre-release version: {latestPreReleaseVersion}. Pre-releases are intended for testing purposes, not regular deployments. Proceed with caution.</span>
                        </RadioButtonGroup>
                    </>)}
                {selectedUsageConstraint === UsageConstraint.Stable && !isOnlyPreRelease && (<RadioButtonGroup value={selectedUpdateBehaviour} onChange={onUpdateBehaviourChanged} title="Select update behaviour" accessibleName="Update behaviour">
                        <RadioButton label="Accept minor changes" value={UpdateBehaviour.Minor}/>
                        <span className={styles.helperText}>Automatically update the template whenever a patch or minor version is published.</span>
                        <RadioButton label="Accept patches" value={UpdateBehaviour.Patch}/>
                        <span className={styles.helperText}>Automatically update the template whenever a patch is published.</span>
                    </RadioButtonGroup>)}
            </ExpandableFormSection>
        </>);
}
const styles = {
    helperText: css({ fontSize: fontSize.xSmall, color: themeTokens.color.text.secondary, marginBottom: space[12] }),
    templateVersionTitle: css({ display: "inline-flex", alignItems: "center", gap: space[4] }),
};
